import { useState, useEffect } from "react";
import Login from "./Login.js";
import NavBar from "./NavBar.js";
import BrandSetup from "./BrandSetup.js";
import ProductCatalogue from "./ProductCatalogue.js";
import Billing from "./Billing.js";
import InvoicePreview from "./InvoicePreview.js";
import Settings from "./Settings.js";
import InvoiceHistory from "./InvoiceHistory.js";
import SalesReport from "./SalesReport.js";
import Customers from "./Customers.js";
import { LIGHT, DARK, buildCSS, DEFAULT_SETTINGS, DEFAULT_BRAND, CURRENCIES, fmtCurrency } from "./utils.js";

// ══════════════════════════════════════════════════════════════════════════════
// DATABASE IMPORTS (UPDATED FOR SUPABASE)
// ══════════════════════════════════════════════════════════════════════════════

import { 
  // Auth
  getCurrentUser,
  getSession,
  onAuthStateChange,
  signOut,
  
  // Subscription
  getUserSubscription,
  
  // Brand & Settings
  getCurrentBrandInfo,
  getCurrentSettings,
  
  // Products
  getAllProducts,
  
  // Invoices
  createInvoice,
  getInvoice,
  updateInvoice,
  deleteInvoice,
  
  // Payments
  addPayment,
  getPaymentStatus
} from "./db/index.js";

// ══════════════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function App() {
  const [darkMode, setDarkMode] = useState(false);
  const G = darkMode ? DARK : LIGHT;
  const [prevStep, setPrevStep] = useState(2);

  const [user, setUser] = useState(null);
  const [step, setStep] = useState(2);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [products, setProducts] = useState([]);

  // Invoice management
  const [invoiceData, setInvoiceData] = useState(null);
  const [savedBill, setSavedBill] = useState(null);

  // Edit mode tracking
  const [editMode, setEditMode] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Loaded flag — prevents save useEffects from firing before load completes
  const [loaded, setLoaded] = useState(false);

  // Payment popup state
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState(null);
  const [partialAmount, setPartialAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [popupStatus, setPopupStatus] = useState(null); // 'paid', 'partial', or null

  // Add Payment modal state
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [addPaymentInvoice, setAddPaymentInvoice] = useState(null);
  const [addPaymentAmount, setAddPaymentAmount] = useState("");
  const [alertModal, setAlertModal] = useState(null);

  // Transfer/Refund modal state (NEW)
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferModalData, setTransferModalData] = useState(null);

  const PAYMENT_MODES = ["Cash", "Card (Debit/Credit)", "UPI", "Net Banking", "NEFT / RTGS", "Cheque", "Bank Transfer", "Other"];
  
  // ══════════════════════════════════════════════════════════════════════════════
  // SESSION CHECK & AUTH STATE LISTENER
  // ══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    // Check for existing session on mount
    const checkExistingSession = async () => {
      try {
        const session = await getSession();
        if (session) {
          await handleSuccessfulAuth();
        }
      } catch (error) {
        console.error("Session check error:", error);
      }
    };

    checkExistingSession();

    // Listen to auth state changes
    const { data: authListener } = onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        await handleSuccessfulAuth();
      }
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setStep(2); // Will show Login component
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Handle successful authentication
  const handleSuccessfulAuth = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) return;

      // Get subscription/tier info
      let subscription;
      try {
        subscription = await getUserSubscription();
      } catch (error) {
        console.error("Subscription fetch error:", error);
        subscription = {
          tierName: "FREE",
          tierLevel: 0, // ← Changed from 1 to 0 for FREE tier
          features: {},
          usage: {
            invoicesThisMonth: 0,
            invoiceLimit: 10,
            productsTotal: 0,
            productLimit: 5, // ← Changed from 25 to 5 for FREE tier
            customersTotal: 0,
            customerLimit: 5  // ← Changed from 25 to 5 for FREE tier
          }
        };
      }

      setUser({
        id: currentUser.id,
        email: currentUser.email,
        name: currentUser.user_metadata?.full_name || 
              currentUser.user_metadata?.name || 
              currentUser.email.split('@')[0],
        tier: subscription.tierName,
        tierLevel: subscription.tierLevel,
        features: subscription.features,
        usage: subscription.usage
      });

    } catch (error) {
      console.error("Auth completion error:", error);
    }
  };
  
  // ══════════════════════════════════════════════════════════════════════════════
  // DATA LOADING FROM DATABASE
  // ══════════════════════════════════════════════════════════════════════════════

  // Load data when user is authenticated
useEffect(() => {
  if (!user) return; // Only load if authenticated

  const loadData = async () => {
    try {
      // Load brand and settings (PAID tier only - FREE tier uses localStorage)
if (user.tierLevel > 0) {
  try {
    const brandInfo = await getCurrentBrandInfo();
    if (brandInfo) {
      setBrand(brandInfo);
    } else {
      console.warn("⚠️ No brand info found - user can set up later");
    }
  } catch (error) {
    console.error("Failed to load brand:", error);
  }

  try {
    const settingsInfo = await getCurrentSettings();
    if (settingsInfo) {
      setSettings(settingsInfo);
    } else {
      console.warn("⚠️ No settings found - user can configure later");
    }
  } catch (error) {
    console.error("Failed to load settings:", error);
  }
} else {
  // FREE tier - load from localStorage if available
  try {
    const savedBrand = localStorage.getItem('brand_free_tier');
    if (savedBrand) {
      setBrand(JSON.parse(savedBrand));
      console.log("✓ Loaded brand from localStorage (FREE tier)");
    }
  } catch (error) {
    console.error("Failed to load brand from localStorage:", error);
  }

  try {
    const savedSettings = localStorage.getItem('settings_free_tier');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
      console.log("✓ Loaded settings from localStorage (FREE tier)");
    }
  } catch (error) {
    console.error("Failed to load settings from localStorage:", error);
  }
}
      
      // Load products (only for PAID tier)
      if (user.tierLevel > 0) {
        try {
          const allProducts = await getAllProducts();
          setProducts(allProducts);
        } catch (productError) {
          console.error("Failed to load products:", productError);
          // Don't block app load if products fail
          setProducts([]);
        }
      }
      
      // Load dark mode from localStorage
      const savedDarkMode = localStorage.getItem("darkMode");
      if (savedDarkMode !== null) {
        setDarkMode(savedDarkMode === 'true');
      }
      
    } catch (error) {
      console.error("❌ Error loading user data:", error);
      
      // Only alert on CRITICAL errors (database connection, auth issues)
      // Not on missing data (fresh users)
      if (error.message?.includes('network') || 
          error.message?.includes('connection') ||
          error.message?.includes('authentication')) {
        alert("Connection error. Please check your internet and refresh the page.");
      } else {
        // Log but don't block - user can still use app with defaults
        console.warn("Some data couldn't be loaded. Using defaults. Error:", error.message);
      }
    }
    
    setLoaded(true);
  };
  
  loadData();
}, [user]);

  // Save dark mode to localStorage
  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode, loaded]);

 // Refresh data when navigating between pages
useEffect(() => {
  const refreshData = async () => {
    if (!loaded || !user) return;
    
    // Refresh brand when entering OR leaving Brand Setup
    if (step === 0 || prevStep === 0) {
      if (user.tierLevel > 0) {
        // PAID tier - fetch from database
        try {
          const brandInfo = await getCurrentBrandInfo();
          if (brandInfo) {
            setBrand(brandInfo);
          }
        } catch (error) {
          console.error("Failed to refresh brand:", error);
        }
      }
      // FREE tier - brand managed in BrandSetup component via localStorage
    }
    
    // Refresh products when entering OR leaving Product Catalogue (PAID tier only)
    if ((step === 1 || prevStep === 1) && user.tierLevel > 0) {
      try {
        const freshProducts = await getAllProducts();
        setProducts(freshProducts);
      } catch (error) {
        console.error("Failed to refresh products:", error);
      }
    }
    
    // Refresh settings when entering OR leaving Settings
    if (step === 6 || prevStep === 6) {
      if (user.tierLevel > 0) {
        // PAID tier - fetch from database
        try {
          const settingsInfo = await getCurrentSettings();
          if (settingsInfo) {
            setSettings(settingsInfo);
          }
        } catch (error) {
          console.error("Failed to refresh settings:", error);
        }
      }
      // FREE tier - settings managed in Settings component via localStorage
    }
    
    setPrevStep(step);
  };
  
  refreshData();
}, [step, loaded, user]);

  // ══════════════════════════════════════════════════════════════════════════════
  // REFRESH SUBSCRIPTION ON NAVIGATION (Prevents expired users from accessing paid features)
  // ══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    const refreshSubscription = async () => {
      if (!loaded || !user) return;
      
      try {
        const freshSubscription = await getUserSubscription();
        
        // Check if tier changed (expired subscription)
        if (freshSubscription.tierLevel !== user.tierLevel) {
          console.warn('⚠️ Subscription tier changed:', {
            old: `${user.tier} (Level ${user.tierLevel})`,
            new: `${freshSubscription.tierName} (Level ${freshSubscription.tierLevel})`
          });
          
          // Update user state with fresh subscription
          setUser(prev => ({
            ...prev,
            tier: freshSubscription.tierName,
            tierLevel: freshSubscription.tierLevel,
            features: freshSubscription.features,
            usage: freshSubscription.usage
          }));
        }
      } catch (error) {
        console.error('Failed to refresh subscription:', error);
      }
    };
    
    refreshSubscription();
  }, [step, loaded, user?.tierLevel]);

    // ══════════════════════════════════════════════════════════════════════════════
  // REFRESH SUBSCRIPTION ON NAVIGATION (Prevents expired users from accessing paid features)
  // ══════════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    const refreshSubscription = async () => {
      if (!loaded || !user) return;
      
      try {
        const freshSubscription = await getUserSubscription();
        
        // Check if tier changed (expired subscription)
        if (freshSubscription.tierLevel !== user.tierLevel) {
          console.warn('⚠️ Subscription tier changed:', {
            old: `${user.tier} (Level ${user.tierLevel})`,
            new: `${freshSubscription.tierName} (Level ${freshSubscription.tierLevel})`
          });
          
          // Update user state with fresh subscription
          setUser(prev => ({
            ...prev,
            tier: freshSubscription.tierName,
            tierLevel: freshSubscription.tierLevel,
            features: freshSubscription.features,
            usage: freshSubscription.usage
          }));
        }
      } catch (error) {
        console.error('Failed to refresh subscription:', error);
      }
    };
    
    refreshSubscription();
  }, [step, loaded, user?.tierLevel]);
  
  // Scroll to top on page/step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // ══════════════════════════════════════════════════════════════════════════════
  // INVOICE GENERATION & EDITING (UPDATED FOR TIER-BASED ROUTING)
  // ══════════════════════════════════════════════════════════════════════════════

  const handleGenerate = async (invoice, metadata = {}) => {
    if (editMode && editingInvoice) {
      // ═══════════════════════════════════════════════════════════════════════════
      // EDIT MODE: Check payments and show transfer/refund modal if needed
      // (Only accessible for PAID tier users)
      // ═══════════════════════════════════════════════════════════════════════════
      
      const paymentStatus = await getPaymentStatus(editingInvoice.id);
      
      if (paymentStatus.amountPaid > 0) {
        // Show transfer/refund modal
        setTransferModalData({
          editingInvoice,
          newInvoiceData: invoice,
          paymentStatus
        });
        setShowTransferModal(true);
      } else {
        // No payments - direct edit
        await performInvoiceEdit(invoice, false);
      }
      
    } else {
      // ═══════════════════════════════════════════════════════════════════════════
      // NEW INVOICE - Tier-based routing
      // ═══════════════════════════════════════════════════════════════════════════
      
      if (metadata.isFREE) {
        // ───────────────────────────────────────────────────────────────────────
        // FREE TIER - Direct to preview (no database, no payment popup)
        // ───────────────────────────────────────────────────────────────────────
        
        setInvoiceData(invoice);
        setStep(3);
        
      } else {
        // ───────────────────────────────────────────────────────────────────────
        // PAID TIER - Load full invoice and show payment popup
        // ───────────────────────────────────────────────────────────────────────
        
        // Load full invoice with payment data
        const fullInvoice = await loadFullInvoiceForDisplay(invoice.id);
        
        // Store pending invoice for payment popup
        setPendingInvoice(fullInvoice);
        setPartialAmount("");
        setPaymentMode("");
        setPaymentDetails("");
        setPopupStatus(null);
        setShowPaymentPopup(true);
      }
    }
  };

  // Perform invoice edit (called from transfer/refund modal or directly)
  const performInvoiceEdit = async (data, markAsRefunded) => {
    try {
      const result = await updateInvoice(
        editingInvoice.id,
        data,
        editingInvoice.version,
        markAsRefunded
      );
      
      if (!result.success) {
        alert(`Failed to edit invoice: ${result.error}`);
        return;
      }
      
      // Load full invoice with payments and snapshots
      const fullInvoice = await loadFullInvoiceForDisplay(result.invoice.id);
      setInvoiceData(fullInvoice);
      
      setEditMode(false);
      setEditingInvoice(null);
      setSavedBill(null);
      setStep(3);
      
    } catch (error) {
      console.error("Invoice edit error:", error);
      alert("Failed to edit invoice. Please try again.");
    }
  };

  // Helper: Load invoice with snapshots hydrated (for display)
  const loadFullInvoiceForDisplay = async (invoiceId) => {
    const invoice = await getInvoice(invoiceId);
    const paymentStatus = await getPaymentStatus(invoiceId);
    
    return {
      ...invoice,
      seller: invoice.brandSnapshot,
      formatSettings: invoice.settingsSnapshot,
      terms: invoice.settingsSnapshot?.notesSections || [],
      amountPaid: paymentStatus.amountPaid,
      paymentStatus: paymentStatus.status
    };
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // PAYMENT HANDLERS
  // ══════════════════════════════════════════════════════════════════════════════

  // Handle payment status selection from popup (for new invoices)
  const handlePaymentSelect = async (paymentType) => {
    if (!pendingInvoice) return;
    
    try {
      if (paymentType === "paid") {
        // Full payment
        await addPayment(pendingInvoice.id, {
          amount: pendingInvoice.grandTotal,
          paidAt: new Date().toISOString(),
          paymentMode: paymentMode || "",
          paymentDetails: paymentDetails || ""
        });
        
      } else if (paymentType === "partial") {
        // Partial payment
        const amount = parseFloat(partialAmount);
        
        if (!partialAmount || isNaN(amount) || amount <= 0) {
          setAlertModal({
            title: "Invalid Amount",
            message: "Enter a valid payment amount greater than zero"
          });
          return;
        }
        
        if (amount > pendingInvoice.grandTotal) {
          setAlertModal({
            title: "Amount Exceeds Total",
            message: `Amount received cannot exceed total invoice value`
          });
          return;
        }
        
        await addPayment(pendingInvoice.id, {
          amount,
          paidAt: new Date().toISOString(),
          paymentMode: paymentMode || "",
          paymentDetails: paymentDetails || ""
        });
        
      } else {
        // Unpaid - no payment record needed
      }
      
      // Load full invoice with payment data
      const fullInvoice = await loadFullInvoiceForDisplay(pendingInvoice.id);
      setInvoiceData(fullInvoice);
      
      // Reset popup state
      setShowPaymentPopup(false);
      setPendingInvoice(null);
      setPartialAmount("");
      setPaymentMode("");
      setPaymentDetails("");
      setPopupStatus(null);
      
      setStep(3);
      
    } catch (error) {
      console.error("Payment error:", error);
      alert("Failed to record payment. Please try again.");
    }
  };

  // Add payment to existing invoice
  const handleAddPayment = async (invoice) => {
    const paymentStatus = await getPaymentStatus(invoice.id);
    
    if (paymentStatus.outstanding <= 0) {
      setAlertModal({
        title: "Invoice Fully Paid",
        message: "This invoice is already fully paid."
      });
      return;
    }
    
    setAddPaymentInvoice(invoice);
    setAddPaymentAmount("");
    setShowAddPaymentModal(true);
  };

  // Handle saving payment from modal
  const handleSavePayment = async () => {
    if (!addPaymentInvoice) return;
    
    const amount = parseFloat(addPaymentAmount);
    const paymentStatus = await getPaymentStatus(addPaymentInvoice.id);
    
    if (!addPaymentAmount || isNaN(amount) || amount <= 0) {
      setAlertModal({
        title: "Invalid Amount",
        message: "Please enter a valid amount greater than zero."
      });
      return;
    }
    
    if (amount > paymentStatus.outstanding) {
      setAlertModal({
        title: "Amount Exceeds Outstanding",
        message: `Payment cannot exceed outstanding amount`
      });
      return;
    }
    
    try {
      await addPayment(addPaymentInvoice.id, {
        amount,
        paidAt: new Date().toISOString(),
        paymentMode: "Cash", // Could add fields for this in modal
        paymentDetails: ""
      });
      
      // Refresh invoice data if currently viewing
      if (invoiceData?.id === addPaymentInvoice.id) {
        const updated = await loadFullInvoiceForDisplay(addPaymentInvoice.id);
        setInvoiceData(updated);
      }
      
      // Close modal
      setShowAddPaymentModal(false);
      setAddPaymentInvoice(null);
      setAddPaymentAmount("");
      
    } catch (error) {
      console.error("Payment error:", error);
      alert("Failed to add payment. Please try again.");
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // INVOICE MANAGEMENT
  // ══════════════════════════════════════════════════════════════════════════════

  // Soft delete invoice
  const handleDelete = async (invoice) => {
    if (!window.confirm(
      `Delete invoice ${invoice.invNo}?\n\n` +
      `It will be hidden from history and reports but kept in records for audit purposes.`
    )) {
      return;
    }
    
    try {
      await deleteInvoice(invoice.id);
      
      // If currently viewing, go back
      if (invoiceData?.id === invoice.id) {
        setInvoiceData(null);
        setStep(4);
      }
      
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete invoice.");
    }
  };

  // Edit existing invoice
  const handleEdit = async (invoice) => {
    // Load full invoice with lines
    const fullInvoice = await getInvoice(invoice.id);
    
    setEditMode(true);
    setEditingInvoice(fullInvoice);
    
    // Prepare saved bill for Billing component
    setSavedBill({
      customer: fullInvoice.customer,
      items: fullInvoice.lines.map(line => ({
        ...line,
        itemDiscount: line.itemDiscount || { type: "pct", value: "" }
      })),
      invNo: fullInvoice.invNo,
      invDate: fullInvoice.invDate,
      txType: fullInvoice.txType || "intra",
      overallDiscount: fullInvoice.overallDiscount || { type: "pct", value: "" }
    });
    
    setStep(2); // Go to Billing
  };

  // Start new invoice (exit edit mode)
  const handleNewInvoice = () => {
    setEditMode(false);
    setEditingInvoice(null);
    setSavedBill(null);
    setStep(2);
  };

  // Edit invoice from preview
  const handleEditFromPreview = () => {
    if (invoiceData) {
      handleEdit(invoiceData);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════════

  if (!user) {
    return (
      <>
        <style>{buildCSS(LIGHT)}</style>
        <Login G={LIGHT} onLogin={(u) => { setUser(u); setStep(2); }} />
      </>
    );
  }

  return (
    <>
      <style>{buildCSS(G)}</style>
      
      <NavBar 
        G={G} 
        onLogout={async () => {
          await signOut();
          setUser(null);
        }} 
        onNav={(stepNumber) => {
          if (stepNumber === 2) {
            setEditMode(false);
            setEditingInvoice(null);
            setSavedBill(null);
          }
          setStep(stepNumber);
        }}
        darkMode={darkMode} 
        setDarkMode={setDarkMode} 
        step={step}
        user={user}
      />

      {/* Transfer/Refund Modal */}
      {showTransferModal && transferModalData && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.bg, borderRadius: 12, padding: "28px",
            maxWidth: 520, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Payment Transfer Options
            </div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 20, lineHeight: 1.5 }}>
              This invoice has received{" "}
              <strong style={{ color: G.ink }}>
                {fmtCurrency(
                  transferModalData.paymentStatus.amountPaid,
                  brand.currency === "CUSTOM" 
                    ? brand.customCurrencySymbol 
                    : CURRENCIES.find(c => c.value === brand.currency)?.symbol,
                  settings.currencyFormat,
                  brand.currency,
                  settings.amountGrouping,
                  settings.amountSeparator,
                  settings.amountDecimals
                )}
              </strong>{" "}
              in payments. How would you like to handle them?
            </div>

            {/* Transfer Button */}
            <button
              onClick={async () => {
                setShowTransferModal(false);
                await performInvoiceEdit(transferModalData.newInvoiceData, false);
                setTransferModalData(null);
              }}
              style={{
                width: "100%",
                padding: "14px 20px",
                border: "1.8px solid rgba(34, 197, 94, 0.35)",
                background: "rgba(34, 197, 94, 0.06)",
                borderRadius: 8,
                color: "#166534",
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 12,
                transition: "all .15s",
                textAlign: "left"
              }}
              onMouseEnter={e => {
                e.target.style.background = "rgba(34, 197, 94, 0.15)";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={e => {
                e.target.style.background = "rgba(34, 197, 94, 0.06)";
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                ✅ Transfer Payments to New Invoice
              </div>
              <div style={{ fontSize: 12, color: G.muted }}>
                Move all payments to the edited invoice (recommended)
              </div>
            </button>

            {/* Refund Button */}
            <button
              onClick={async () => {
                setShowTransferModal(false);
                await performInvoiceEdit(transferModalData.newInvoiceData, true);
                setTransferModalData(null);
              }}
              style={{
                width: "100%",
                padding: "14px 20px",
                border: "1.8px solid rgba(239, 68, 68, 0.35)",
                background: "rgba(239, 68, 68, 0.06)",
                borderRadius: 8,
                color: "#991B1B",
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 20,
                transition: "all .15s",
                textAlign: "left"
              }}
              onMouseEnter={e => {
                e.target.style.background = "rgba(239, 68, 68, 0.15)";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={e => {
                e.target.style.background = "rgba(239, 68, 68, 0.06)";
                e.target.style.transform = "translateY(0)";
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                💸 Mark as Refunded
              </div>
              <div style={{ fontSize: 12, color: G.muted }}>
                Customer was reimbursed (payments won't transfer)
              </div>
            </button>

            {/* Cancel */}
            <button
              onClick={() => {
                setShowTransferModal(false);
                setTransferModalData(null);
              }}
              style={{
                width: "100%",
                padding: "10px 16px",
                border: `1px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface2,
                color: G.ink,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Payment Status Popup */}
      {showPaymentPopup && pendingInvoice && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.bg, borderRadius: 12, padding: "28px",
            maxWidth: 520, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Mark Payment Status
            </div>
            <div style={{ fontSize: 13, color: G.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Invoice Total: <strong style={{ color: G.ink }}>
                {fmtCurrency(
                  pendingInvoice.grandTotal,
                  pendingInvoice.seller?.currency === "CUSTOM" 
                    ? pendingInvoice.seller?.customCurrencySymbol 
                    : CURRENCIES.find(c => c.value === pendingInvoice.seller?.currency)?.symbol,
                  pendingInvoice.formatSettings?.currencyFormat,
                  pendingInvoice.seller?.currency,
                  pendingInvoice.formatSettings?.amountGrouping,
                  pendingInvoice.formatSettings?.amountSeparator,
                  pendingInvoice.formatSettings?.amountDecimals
                )}
              </strong>
            </div>

            {/* Payment Status Buttons - Horizontal */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              {/* Unpaid Button */}
              <button
                onClick={() => handlePaymentSelect("unpaid")}
                style={{
                  flex: 1,
                  padding: "16px 12px",
                  border: "1.8px solid rgba(239, 68, 68, 0.35)",
                  background: "rgba(239, 68, 68, 0.06)",
                  borderRadius: 8,
                  color: "#991B1B",
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  transition: "all .15s"
                }}
                onMouseEnter={e => {
                  e.target.style.background = "rgba(239, 68, 68, 0.15)";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={e => {
                  e.target.style.background = "rgba(239, 68, 68, 0.06)";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                <span>Unpaid</span>
              </button>

              {/* Partially Paid Button */}
              <button
                onClick={() => {
                  setPopupStatus("partial");
                  setPartialAmount("");
                }}
                style={{
                  flex: 1,
                  padding: "16px 12px",
                  border: popupStatus === "partial" ? "1.8px solid #EAB308" : "1.8px solid rgba(234, 179, 8, 0.35)",
                  background: popupStatus === "partial" ? "rgba(234, 179, 8, 0.15)" : "rgba(234, 179, 8, 0.06)",
                  borderRadius: 8,
                  color: "#854D0E",
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  transition: "all .15s"
                }}
                onMouseEnter={e => {
                  if (popupStatus !== "partial") {
                    e.target.style.background = "rgba(234, 179, 8, 0.15)";
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={e => {
                  if (popupStatus !== "partial") {
                    e.target.style.background = "rgba(234, 179, 8, 0.06)";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                <span>Partially Paid</span>
              </button>

              {/* Paid Button */}
              <button
                onClick={() => setPopupStatus("paid")}
                style={{
                  flex: 1,
                  padding: "16px 12px",
                  border: popupStatus === "paid" ? "1.8px solid #22C55E" : "1.8px solid rgba(34, 197, 94, 0.35)",
                  background: popupStatus === "paid" ? "rgba(34, 197, 94, 0.15)" : "rgba(34, 197, 94, 0.06)",
                  borderRadius: 8,
                  color: "#166534",
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  transition: "all .15s"
                }}
                onMouseEnter={e => {
                  if (popupStatus !== "paid") {
                    e.target.style.background = "rgba(34, 197, 94, 0.15)";
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={e => {
                  if (popupStatus !== "paid") {
                    e.target.style.background = "rgba(34, 197, 94, 0.06)";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                <span>Paid</span>
              </button>
            </div>

            {/* Expanding Fields for Partial or Paid */}
            {popupStatus && (
              <div style={{ 
                background: G.surface, 
                border: `1px solid ${G.border}`, 
                borderRadius: 8, 
                padding: 16, 
                marginBottom: 16 
              }}>
                {/* Amount Received (Only for Partial) */}
                {popupStatus === "partial" && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{
                      fontSize: 11, fontWeight: 600, color: G.muted,
                      textTransform: "uppercase", letterSpacing: .6,
                      display: "block", marginBottom: 6
                    }}>
                      Amount Received
                    </label>
                    <input
                      type="number"
                      placeholder="Enter amount"
                      value={partialAmount}
                      onChange={e => setPartialAmount(e.target.value)}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: `1.5px solid ${G.border}`,
                        borderRadius: 7,
                        background: G.bg,
                        color: G.ink,
                        fontFamily: "'Sora', sans-serif",
                        fontSize: 14,
                        outline: "none"
                      }}
                    />
                  </div>
                )}

                {/* Payment Mode */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Payment Mode
                  </label>
                  <select
                    value={paymentMode}
                    onChange={e => setPaymentMode(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.bg,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none",
                      cursor: "pointer"
                    }}
                  >
                    <option value="">Select payment mode</option>
                    {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                {/* Payment Details */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    fontSize: 11, fontWeight: 600, color: G.muted,
                    textTransform: "uppercase", letterSpacing: .6,
                    display: "block", marginBottom: 6
                  }}>
                    Payment Details (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Transaction ID, Cheque Number, Bank Name"
                    value={paymentDetails}
                    onChange={e => setPaymentDetails(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `1.5px solid ${G.border}`,
                      borderRadius: 7,
                      background: G.bg,
                      color: G.ink,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: 14,
                      outline: "none"
                    }}
                  />
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handlePaymentSelect(popupStatus)}
                  style={{
                    width: "100%",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: 7,
                    background: G.accent,
                    color: G.accentFg,
                    cursor: "pointer",
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: "nowrap"
                  }}
                >
                  Add Payment
                </button>
              </div>
            )}

            {/* Cancel */}
            <button
              onClick={() => {
                setShowPaymentPopup(false);
                setPendingInvoice(null);
                setPartialAmount("");
                setPopupStatus(null);
              }}
              style={{
                width: "100%",
                padding: "10px 16px",
                border: `1px solid ${G.border}`,
                borderRadius: 7,
                background: G.surface2,
                color: G.ink,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddPaymentModal && addPaymentInvoice && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "28px",
            maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: G.ink }}>
              Add Payment
            </div>
            
            {/* Invoice Details */}
            <div style={{ 
              background: G.surface2, 
              borderRadius: 8, 
              padding: "14px 16px", 
              marginBottom: 20,
              border: `1px solid ${G.border}`
            }}>
              <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
                Invoice: <strong style={{ color: G.ink }}>{addPaymentInvoice.invNo}</strong>
              </div>
              <div style={{ fontSize: 13, color: G.muted, marginBottom: 8 }}>
                Customer: <strong style={{ color: G.ink }}>{addPaymentInvoice.customer?.name}</strong>
              </div>
              
              <div style={{ 
                height: 1, 
                background: G.border, 
                margin: "10px 0" 
              }} />
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, fontSize: 12 }}>
                <div>
                  <div style={{ color: G.muted, marginBottom: 3 }}>Total</div>
                  <div style={{ fontWeight: 700, color: G.accent }}>
                    {addPaymentInvoice.grandTotal?.toFixed(2) || "0.00"}
                  </div>
                </div>
                <div>
                  <div style={{ color: G.muted, marginBottom: 3 }}>Already Paid</div>
                  <div style={{ fontWeight: 700, color: "#22C55E" }}>
                    {(addPaymentInvoice.amountPaid || 0).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: G.muted, marginBottom: 3 }}>Outstanding</div>
                  <div style={{ fontWeight: 700, color: "#EF4444" }}>
                    {((addPaymentInvoice.grandTotal || 0) - (addPaymentInvoice.amountPaid || 0)).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Amount Input */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ 
                fontSize: 11, 
                fontWeight: 600, 
                color: G.muted, 
                textTransform: "uppercase", 
                letterSpacing: .6, 
                display: "block", 
                marginBottom: 6 
              }}>
                Payment Amount
              </label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="Enter amount received"
                value={addPaymentAmount}
                onChange={e => {
                  let val = e.target.value.replace(/[^\d.]/g, '');
                  if (val === '.') val = '0.';
                  const parts = val.split('.');
                  if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                  if (parts[1] && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
                  setAddPaymentAmount(val);
                }}
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  border: `1.5px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface,
                  color: G.ink,
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 15,
                  fontWeight: 600
                }}
              />
              <div style={{ fontSize: 11, color: G.muted, marginTop: 6 }}>
                Maximum: {((addPaymentInvoice.grandTotal || 0) - (addPaymentInvoice.amountPaid || 0)).toFixed(2)}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  setShowAddPaymentModal(false);
                  setAddPaymentInvoice(null);
                  setAddPaymentAmount("");
                }}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  border: `1px solid ${G.border}`,
                  borderRadius: 7,
                  background: G.surface2,
                  color: G.ink,
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: 7,
                  background: "#22C55E",
                  color: "#FFFFFF",
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: 13,
                  fontWeight: 600
                }}
              >
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 10000
        }}>
          <div style={{
            background: G.surface, borderRadius: 12, padding: "24px",
            maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
          }}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 22 }}>
              <div style={{ 
                width: 36, height: 36, borderRadius: "50%", 
                background: G.dangerBg, color: G.danger, 
                display: "flex", alignItems: "center", justifyContent: "center", 
                fontSize: 18, fontWeight: 700, flexShrink: 0 
              }}>
                !
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: G.ink, marginBottom: 6 }}>
                  {alertModal.title}
                </div>
                <div style={{ fontSize: 16, color: G.ink2, lineHeight: 1.5 }}>
                  {alertModal.message}
                </div>
              </div>
            </div>

            <button
              onClick={() => setAlertModal(null)}
              style={{
                width: "100%",
                padding: "11px 16px",
                border: "none",
                borderRadius: 7,
                background: G.ink,
                color: G.surface,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                fontSize: 13,
                fontWeight: 600
              }}
            >
              OK
            </button>
          </div>
        </div>
      )} 
      
      {/* Pages */}
      {step === 0 && (
        <BrandSetup 
          G={G} 
          brand={brand} 
          setBrand={setBrand} 
          onDone={() => setStep(2)} 
          settings={settings}
          user={user}
        />
      )}
      
      {step === 1 && (
        <ProductCatalogue 
          G={G} 
          products={products} 
          setProducts={setProducts} 
          brand={brand} 
          settings={settings}
          setSettings={setSettings}
          user={user}
        />
      )}
      
      {step === 2 && (
        <Billing 
          G={G} 
          brand={brand}
          settings={settings}
          onGenerate={handleGenerate} 
          savedBill={savedBill} 
          setSavedBill={setSavedBill}
          editMode={editMode}
          editingInvoice={editingInvoice}
          user={user}
        />
      )}
      
      {step === 3 && invoiceData && (
        <InvoicePreview 
          G={G} 
          invoiceId={user.tierLevel === 0 ? undefined : invoiceData.id}
          invoiceData={user.tierLevel === 0 ? invoiceData : undefined}
          user={user}
          onEditInvoice={handleEditFromPreview} 
          onNewInvoice={handleNewInvoice}
          onDeleteInvoice={handleDelete}
        />
      )}
      
      {step === 4 && (
        <InvoiceHistory 
          G={G} 
          brand={brand}
          settings={settings}
          onView={(invoice) => {
            setInvoiceData(invoice);
            setStep(3);
          }} 
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddPayment={handleAddPayment}
          user={user}
        />
      )}
      
      {step === 5 && (
        <SalesReport 
          G={G} 
          brand={brand}
          user={user}
        />
      )}
      
      {step === 6 && (
        <Settings 
          G={G} 
          settings={settings} 
          setSettings={setSettings}
          brand={brand}
          setBrand={setBrand} 
          darkMode={darkMode} 
          setDarkMode={setDarkMode}
          user={user}
        />
      )}

      {/* CUSTOMERS PAGE - STEP 7 */}
      {step === 7 && (
        <Customers 
          G={G} 
          brand={brand}
          settings={settings}
          user={user}
        />
      )}
    </>
  );
}